import type { CSSProperties } from 'react';

// ─────────────────────────────────────────────────────────────────────
// deviceFillBoxStyle — the single source of truth for the physical-pad kiosk
// "fill" look. Every Banepad pad frame (the idle/booking status frame, the
// Padel24/Americano/Club CourtPadCard, the KM/Seriespill KMCourtCard, and the
// empty-state boxes) is built as a fixed 4:3 "iPad screen" for Sentralen. On the
// device we want them ALL to look the same: edge-to-edge on the 16:10 tablet, no
// 4:3 letterbox bars, no size caps, no rounded border.
//
// Spread this AFTER a frame's base style so it overrides aspectRatio / maxWidth /
// maxHeight / border / radius when `fill` is set. Returns {} when not filling, so
// Sentralen and the stacked preview are completely unchanged.
//
// `position: fixed; inset: 0` pins the frame to the whole viewport (height comes
// from top/bottom: 0, so the 4:3 aspect lock no longer drives it). zIndex keeps
// it above the device wrapper's letterbox backdrop.
// ─────────────────────────────────────────────────────────────────────
export function deviceFillBoxStyle(fill?: boolean): CSSProperties {
  if (!fill) return {};
  return {
    position: 'fixed',
    inset: 0,
    aspectRatio: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    zIndex: 30,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Automatic pad scale for the physical device.
//
// The pads were designed as a 900×675 (4:3) box for Sentralen's preview, and
// the tournament pads (CourtPadCard, KM, Seriespill) size their text in fixed
// pixels — so on a 1280-px-wide Android tablet they render exactly as small as
// in the preview, while the idle/booking screen (cqw units) scales. The device
// root applies ONE CSS `zoom` = the largest factor at which the design box
// still fits the tablet in BOTH dimensions; fixed-px pads grow with the
// tablet, cqw pads are unaffected (their container shrinks by the same
// factor the zoom enlarges). No per-device setting: «modern Android tablet»
// is the only hardware spec, so the scale has to be automatic.
// ─────────────────────────────────────────────────────────────────────
export const PAD_DESIGN_BOX = { width: 900, height: 675 } as const;
export const PAD_ZOOM_MIN = 0.5;
export const PAD_ZOOM_MAX = 3;

/** Pure: the zoom factor for a device viewport of `width`×`height` CSS px. */
export function computePadZoom(width: number, height: number): number {
  if (!(width > 0) || !(height > 0)) return 1;
  const z = Math.min(width / PAD_DESIGN_BOX.width, height / PAD_DESIGN_BOX.height);
  return Math.round(Math.min(PAD_ZOOM_MAX, Math.max(PAD_ZOOM_MIN, z)) * 1000) / 1000;
}
